<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { logger } from "@/shared/lib";
import { UiButton, UiNotification, XlsIcon } from "@/shared/ui";

import type { B2bLeadDevice } from "../api";

import { useApiCreateB2bLead } from "../model";
import DevicesListDropzone from "./DevicesListDropzone.vue";

/** Шаблон XLSX, по которому компания заполняет список устройств */
const DEVICES_LIST_TEMPLATE_URL =
    "https://cdn.new-brz.net/public/static/device-buyout-form-template.xlsx";

/** Устройство, выбранное на прошлых шагах: уходит в лид вместе со списком */
const { device } = defineProps<{ device: B2bLeadDevice }>();

const emit = defineEmits<{ sent: [] }>();

const { t } = useI18n({ useScope: "global" });
const { mutateAsync: uploadList, isLoading: isUploading, error } = useApiCreateB2bLead();

const file = ref<File | null>(null);

const canSubmit = computed(() => Boolean(file.value) && !isUploading.value);

async function submit(): Promise<void> {
    if (!file.value || !canSubmit.value) return;

    try {
        await uploadList({ file: file.value, device });
    } catch (uploadError) {
        logger.error("devices list is not uploaded", uploadError);
        return;
    }

    emit("sent");
}
</script>

<!-- Загрузка списка техники компании для предложения по выкупу -->
<template>
    <form class="form" novalidate @submit.prevent="submit">
        <a
            class="template"
            :href="DEVICES_LIST_TEMPLATE_URL"
            download
            target="_blank"
            rel="noopener noreferrer"
        >
            <XlsIcon class="template__icon" />
            {{ t("uploadDevicesList.downloadTemplate") }}
        </a>

        <DevicesListDropzone v-model="file" :disabled="isUploading" />

        <UiNotification v-if="error" type="danger" class="error">
            {{ t("common.error") }}
        </UiNotification>

        <UiButton type="submit" class="submit" :loading="isUploading" :disabled="!canSubmit">
            {{ t("uploadDevicesList.submit") }}
        </UiButton>

        <div v-if="$slots.back" class="back">
            <slot name="back" />
        </div>
    </form>
</template>

<style scoped>
.form {
    display: flex;
    flex-direction: column;
}

.template {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 12px;
    margin-bottom: 24px;
    color: var(--color-link);
    font-size: var(--custom-font-size);
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.template__icon {
    color: var(--custom-color-gray);
}

.error {
    margin-top: 16px;
}

.submit {
    width: 100%;
    margin-top: 24px;
}

.back {
    margin-top: 16px;
}
</style>
